import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/auth';

/**
 * Protected Route Component
 *
 * Wraps routes that require authentication. Redirects to login if not authenticated.
 * Optionally checks for specific user roles.
 */

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  redirectTo = '/login'
}: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading indicator while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-8">
              You don't have permission to access this page.
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has required role
  return <>{children}</>;
}

/**
 * Admin-only route guard
 */
export function AdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['platform_admin', 'company_admin']}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Company Admin-only route guard
 */
export function CompanyAdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['company_admin']}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Platform Admin-only route guard
 */
export function PlatformAdminRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <ProtectedRoute requiredRoles={['platform_admin']}>
      {children}
    </ProtectedRoute>
  );
}

export default ProtectedRoute;
